import type { ReactNode } from 'react'

import { Flex, Text } from '@enechain/ecloud-designsystem'

/** 値の色の意味。 */
export type ToolbarStatusTone = 'positive' | 'warning' | 'negative'

const TONE_TEXT_COLOR: Record<ToolbarStatusTone, string> = {
  positive: 'Text.positiveHigh',
  warning: 'Text.warningHigh',
  negative: 'Text.negativeMid',
}

/** ツールバーの「ラベル 値」1 つ (件数・今の条件・状況)。 */
export type ToolbarStatusItem = {
  key: string
  label: string
  value: ReactNode
  unit?: string
  tone?: ToolbarStatusTone
  /** hover の説明 (title)。長い値を … で切った時の全文などに使う。 */
  description?: string
}

type Props = {
  items: readonly ToolbarStatusItem[]
  /**
   * 器を作らず、項目を親の折り返しの並びに直接並べる (`display: contents`)。親が同じ間隔
   * (横 20px・縦 8px) で並べる時に使う。
   */
  flowInParent?: boolean
  /** 開閉式の要約は `caption` (12px)。状況は既定の `body` (14px)。 */
  textStyle?: 'body' | 'caption'
}

/**
 * ツールバーの読み取り専用「ラベル 値」(UI 辞書 toolbar-collapsible.md「要約」)。
 * Tab では止まらない。値は太字 + 等幅数字。項目の間は横 20px、折り返した時の縦は 8px。
 */
export const ToolbarStatusItems = ({
  items,
  flowInParent = false,
  textStyle = 'body',
}: Props): React.ReactElement => (
  <Flex
    alignItems="center"
    flexWrap="wrap"
    rowGap={2}
    columnGap="20px"
    display={flowInParent ? 'contents' : undefined}
  >
    {items.map((item) => (
      <Flex key={item.key} alignItems="center" gap={1} title={item.description}>
        <Text
          textStyle={textStyle}
          color="Text.highEmphasis"
          whiteSpace="nowrap"
        >
          {item.label}
        </Text>
        <Text
          textStyle={textStyle}
          color={
            item.tone === undefined ? undefined : TONE_TEXT_COLOR[item.tone]
          }
          fontWeight="bold"
          whiteSpace="nowrap"
          fontVariantNumeric="tabular-nums"
        >
          {item.value}
        </Text>
        {item.unit !== undefined && item.unit !== '' && (
          <Text
            textStyle={textStyle}
            color="Text.midEmphasis"
            whiteSpace="nowrap"
          >
            {item.unit}
          </Text>
        )}
      </Flex>
    ))}
  </Flex>
)
